/**
 * V2 Flexible Layout — Button Section Field Configuration
 *
 * FieldConfig for FlexibleLayout Button sections.
 * Replaces the minimal inline ButtonLabelEditor with a full TextElement setup
 * (text + text styles + button container styles), matching V2ActionBarSection pattern.
 *
 * Proxy paths (via FlexibleSectionContentAdapter):
 *   content.buttonContent        → section.buttonContent (TapTarget)
 *   content.buttonContent.content → section.buttonContent.content (TapTargetContent)
 */

import type { FieldConfig } from '../types/editorFieldTypes';
import {
  createTextStyleFields,
  createButtonContainerStyleFields,
  createV2ButtonAppearanceFields,
  createV2IconFields,
  remapTextFieldsForV2,
  remapButtonStyleFieldsForV2,
} from './atomicFields';
import { createV2ButtonStyleGroups } from './v2ButtonStyleGroups';

/**
 * Button field configuration for FlexibleLayout sections.
 *
 * Path mapping through proxy adapter:
 *   path: 'content.buttonContent'                  → enable/disable (full TapTarget)
 *   l10nPath: 'content.buttonContent.content'      → translations read/write
 *   styleFields: 'content.buttonContent.content.*'  → text appearance (color, font, size)
 *   buttonStyleFields: 'content.buttonContent.*'    → button container (bg, border, dimensions)
 */
export const V2_FLEXIBLE_BUTTON_CONFIG: FieldConfig = {
  path: 'content.buttonContent',
  label: 'Button',
  placeholder: 'Button Text',
  type: 'text',
  l10nPath: 'content.buttonContent.content',
  defaultValue: 'Button',
  disableBehavior: 'null',
  guideKey: 'screenedit-flexible-sections-button-text',
  styleFields: remapTextFieldsForV2(createTextStyleFields('content.buttonContent.content')),
  buttonStyleFields: [
    ...remapButtonStyleFieldsForV2(createButtonContainerStyleFields('content.buttonContent')),
    ...remapButtonStyleFieldsForV2(createV2ButtonAppearanceFields('content.buttonContent')),
    ...createV2IconFields('content.buttonContent'),
  ],
  buttonStyleGroups: createV2ButtonStyleGroups('content.buttonContent'),
  paddingConfig: {
    basePath: 'content.buttonContent.layout.insets',
    label: 'Box Padding',
  },
};
